import type { ErrorEnvelope } from './envelope'
import { ErrorCode, type ErrorCodeValue } from './error-codes'

/**
 * Every failure reaching a component is an ApmpApiError. Components branch on `.code`,
 * never on `.message` and never on the HTTP status.
 */
export class ApmpApiError extends Error {
  readonly code: ErrorCodeValue | string
  readonly status: number
  readonly fieldErrors: Record<string, string[]>
  readonly correlationId?: string
  readonly retryAfter?: number

  constructor(init: {
    message: string
    code: string
    status: number
    fieldErrors?: Record<string, string[]>
    correlationId?: string
    retryAfter?: number
  }) {
    super(init.message)
    this.name = 'ApmpApiError'
    this.code = init.code
    this.status = init.status
    this.fieldErrors = init.fieldErrors ?? {}
    this.correlationId = init.correlationId
    this.retryAfter = init.retryAfter
  }

  get isValidation(): boolean {
    return this.code === ErrorCode.VALIDATION_FAILED
  }

  get isAuth(): boolean {
    return this.status === 401
  }

  get isPermission(): boolean {
    return this.code === ErrorCode.PERMISSION_DENIED
  }

  get isNotFound(): boolean {
    return this.code === ErrorCode.NOT_FOUND
  }

  /** A 409 the user can resolve by reviewing and retrying. */
  get isConflict(): boolean {
    return this.status === 409
  }

  static fromEnvelope(
    body: ErrorEnvelope,
    status: number,
    headers?: { correlationId?: string; retryAfter?: number },
  ): ApmpApiError {
    return new ApmpApiError({
      message: body.message,
      code: body.code,
      status,
      fieldErrors: body.errors ?? {},
      correlationId: headers?.correlationId,
      retryAfter: headers?.retryAfter,
    })
  }

  static network(message = 'We could not reach APMP. Check your connection and try again.') {
    return new ApmpApiError({ message, code: ErrorCode.NETWORK_ERROR, status: 0 })
  }
}
